import { OnboardingActions } from '@portal/actions';
import { generateKeyPair } from '@portal/repositories/securitySdkConfig';
import { maskPhoneInput, unmaskField } from '@portal/services/masks';
import React, { useState } from 'react';
import { browserName } from 'react-device-detect';
import { MessageOutlined, WhatsAppOutlined } from '@ant-design/icons';

import AdvancedInput from '@portal/components/AdvancedInput/AdvancedInput';
import * as StorageService from '~/services/storage';
import { useDispatch } from 'react-redux';
import { translate } from '@portal/services/i18n';
import AdvancedSmsCode from '@portal/components/AdvancedSmsCode/AdvancedSmsCode';
import ThemedText from '@portal/components/ThemedText/ThemedText';
import { TokenMethod } from '@portal/enum/device';

type ApprovalDeviceProps = {
  onFormChange: (key: string, val: string) => void;
  userDataForm: Pick<
    models.OnboardingRequest,
    'cellphone' | 'deviceApprovalCode' | 'account'
  >;
};

const ApprovalDevice = ({
  onFormChange,
  userDataForm,
}: ApprovalDeviceProps) => {
  const [visible, setVisible] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState<TokenMethod | null>(
    null
  );
  const [codeRequested, setCodeRequested] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);
  const dispatch = useDispatch();

  const handleSelectMethod = (method: TokenMethod) => {
    if (isRequesting) return;
    setSelectedMethod(method);
    setIsRequesting(true);
    handleSolicitedSms(false, method);
  };

  const handleSolicitedSms = (sendEmail: boolean, method?: TokenMethod) => {
    const deviceId = StorageService.getDeviceId();
    const { publicKey } = generateKeyPair();
    const methodToUse = method || selectedMethod || TokenMethod.SMS;

    dispatch(
      OnboardingActions.onboardingRegisterDevice({
        deviceInfo: {
          cellphone: `55${unmaskField(userDataForm.cellphone)}`,
          carrier: '',
          deviceId: deviceId ?? '',
          deviceType: 0,
          manufacturer: '',
          model: browserName,
        },
        email:
          sendEmail && userDataForm.account?.email
            ? userDataForm.account?.email
            : '',
        publicKey: publicKey,
        sendEmail,
        method: methodToUse,
      })
    );
    setTimeout(() => {
      setCodeRequested(true);
      setIsRequesting(false);
    }, 500);
  };

  const getMethodLabel = () => {
    if (selectedMethod === TokenMethod.SMS) {
      return translate('PAGES.AUTH.TOKEN_CHANNEL_SELECTION.SMS_BUTTON');
    }
    if (selectedMethod === TokenMethod.WHATSAPP) {
      return translate('PAGES.AUTH.TOKEN_CHANNEL_SELECTION.WHATSAPP_BUTTON');
    }
    return '';
  };

  return (
    <div className="approval-device">
      <div className="approval-device__body">
        {!codeRequested && (
          <div className="approval-device__header">
            <ThemedText variant="body">
              {translate(
                'PAGES.AUTH.REGISTER.ONBOARD.PF.APPROVAL_DEVICE.FORM.CELLPHONE_INFO'
              )}{' '}
              <strong>{maskPhoneInput(userDataForm.cellphone ?? '')}</strong>.
            </ThemedText>
            <ThemedText
              variant="body"
              style={{ marginTop: '8px', display: 'block' }}
            >
              <button
                className="approval-device__header__change-cellphone"
                onClick={() => setVisible(true)}
              >
                {translate(
                  'PAGES.AUTH.REGISTER.ONBOARD.PF.APPROVAL_DEVICE.FORM.BUTTONS.ITS_NOT_YOUR_CELLPHONE'
                )}
              </button>
            </ThemedText>
          </div>
        )}

        {codeRequested && selectedMethod ? (
          <div className="approval-device__header">
            <ThemedText variant="body">
              {translate(
                'PAGES.AUTH.REGISTER.ONBOARD.PF.APPROVAL_DEVICE.FORM.CODE_SENT_INFO'
              )}{' '}
              <strong>{getMethodLabel()}</strong>{' '}
              {translate(
                'PAGES.AUTH.REGISTER.ONBOARD.PF.APPROVAL_DEVICE.FORM.CODE_SENT_INFO_SUFFIX'
              )}{' '}
              <strong>{maskPhoneInput(userDataForm.cellphone ?? '')}</strong>.
            </ThemedText>
          </div>
        ) : null}

        {visible ? (
          <div className="approval-device__change-cellphone">
            <AdvancedInput
              variant="outlined"
              label={translate(
                'PAGES.AUTH.REGISTER.ONBOARD.PF.APPROVAL_DEVICE.FORM.CELLPHONE'
              )}
              value={userDataForm.cellphone}
              onChange={(value) => {
                onFormChange('cellphone', maskPhoneInput(value));
              }}
            />
          </div>
        ) : null}

        {!codeRequested ? (
          <div className="approval-device__method-selection-container">
            <div className="approval-device__method-selection">
              <button
                type="button"
                className={`approval-device__method-card ${
                  isRequesting && selectedMethod === TokenMethod.SMS
                    ? 'approval-device__method-card--loading'
                    : ''
                }`}
                onClick={() => handleSelectMethod(TokenMethod.SMS)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSelectMethod(TokenMethod.SMS);
                  }
                }}
                role="button"
                tabIndex={0}
                disabled={isRequesting}
              >
                <div className="approval-device__method-card__icon approval-device__method-card__icon--sms">
                  <MessageOutlined />
                </div>
                <ThemedText variant="body" fontWeight="bold">
                  {translate('PAGES.AUTH.TOKEN_CHANNEL_SELECTION.SMS_BUTTON')}
                </ThemedText>
              </button>
              <button
                type="button"
                className={`approval-device__method-card ${
                  isRequesting && selectedMethod === TokenMethod.WHATSAPP
                    ? 'approval-device__method-card--loading'
                    : ''
                }`}
                onClick={() => handleSelectMethod(TokenMethod.WHATSAPP)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSelectMethod(TokenMethod.WHATSAPP);
                  }
                }}
                role="button"
                tabIndex={0}
                disabled={isRequesting}
              >
                <div className="approval-device__method-card__icon approval-device__method-card__icon--whatsapp">
                  <WhatsAppOutlined />
                </div>
                <ThemedText variant="body" fontWeight="bold">
                  {translate(
                    'PAGES.AUTH.TOKEN_CHANNEL_SELECTION.WHATSAPP_BUTTON'
                  )}
                </ThemedText>
              </button>
            </div>
          </div>
        ) : (
          <div className="approval-device__code-container">
            <AdvancedSmsCode
              value={userDataForm.deviceApprovalCode ?? ''}
              length={6}
              onChange={(val) => onFormChange('deviceApprovalCode', val)}
            />
            <div className="approval-device__resend-section">
              <ThemedText variant="body">
                {translate(
                  'PAGES.AUTH.REGISTER.ONBOARD.PF.APPROVAL_DEVICE.FORM.NO_CODE'
                )}{' '}
                <button
                  type="button"
                  className="approval-device__resend-button"
                  onClick={() => handleSolicitedSms(false)}
                >
                  {translate(
                    'PAGES.AUTH.REGISTER.ONBOARD.PF.APPROVAL_DEVICE.FORM.BUTTONS.RESEND_SMS'
                  )}
                </button>
              </ThemedText>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApprovalDevice;
